<script setup>
import { computed, onActivated, onDeactivated, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppCampaignStatusBadge from 'dashboard/components-next/Campaigns/CampaignCard/WhatsAppCampaignStatusBadge.vue';
import CampaignAnalyticsSummary from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/CampaignAnalyticsSummary.vue';
import RecipientsTable from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/RecipientsTable.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const campaignId = computed(() => route.params.campaignId);

const TERMINAL_STATUSES = [
  'completed',
  'completed_with_errors',
  'cancelled',
  'failed',
];

const getCampaign = useMapGetter('campaigns/getCampaign');
const analytics = useMapGetter('campaigns/getCampaignAnalytics');
const recipients = useMapGetter('campaigns/getCampaignRecipients');
const recipientsMeta = useMapGetter('campaigns/getCampaignRecipientsMeta');
const uiFlags = useMapGetter('campaigns/getUIFlags');

const campaign = computed(() => getCampaign.value(campaignId.value));
const isTerminal = computed(() =>
  campaign.value
    ? TERMINAL_STATUSES.includes(campaign.value.campaign_status)
    : false
);
const isTransitioning = computed(() => uiFlags.value.isTransitioning);
const isFetchingRecipients = computed(() => uiFlags.value.isFetchingRecipients);

const statusFilter = ref('');
const currentPage = ref(1);

const hasFailedRecipients = computed(
  () => (analytics.value?.status_counts?.failed || 0) > 0
);

const fetchRecipients = () =>
  store.dispatch('campaigns/fetchRecipients', {
    id: campaignId.value,
    status: statusFilter.value || undefined,
    page: currentPage.value,
  });

const fetchAll = async () => {
  await store.dispatch('campaigns/fetchOne', campaignId.value);
  await Promise.all([
    store.dispatch('campaigns/fetchAnalytics', campaignId.value),
    fetchRecipients(),
  ]);
};

const { startRefetching, stopRefetching } = useLiveRefresh(fetchAll, 10000);

// Only keep polling while the campaign can still change on its own
// (processing/scheduled/paused can all still move) - a terminal campaign's
// numbers never change again, so there's nothing left to refresh. Reactive
// rather than checked inline in the poll callback, so it also reacts if the
// backend flips the campaign to terminal between polls.
watch(isTerminal, terminal => {
  if (terminal) stopRefetching();
});

watch([statusFilter, currentPage], fetchRecipients);

// This page renders inside a <keep-alive> router-view (see
// CampaignsPageRouteView.vue), so onBeforeUnmount alone would never fire on
// simple navigation away - onDeactivated is what actually stops polling
// when the user leaves, matching the same pattern already used by
// settings/data/Show.vue for the same reason.
onActivated(async () => {
  await fetchAll();
  if (!isTerminal.value) startRefetching();
});
onDeactivated(stopRefetching);

const goBack = () => router.push({ name: 'campaigns_whatsapp_index' });

const runAction = async (action, id) => {
  try {
    await store.dispatch(action, id);
  } catch (error) {
    useAlert(error?.message || t('CAMPAIGN.WHATSAPP.DETAIL.ACTION_ERROR'));
  }
};

const handlePause = () => runAction('campaigns/pause', campaignId.value);
const handleResume = () => runAction('campaigns/resume', campaignId.value);
const handleCancel = () => runAction('campaigns/cancel', campaignId.value);
const handleRetryFailed = () =>
  runAction('campaigns/retryFailed', campaignId.value);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-3">
            <Button
              icon="i-lucide-arrow-left"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goBack"
            />
            <span class="text-heading-1 text-n-slate-12">
              {{ campaign?.title }}
            </span>
            <WhatsAppCampaignStatusBadge
              v-if="campaign"
              :status="campaign.campaign_status"
            />
          </div>
          <div v-if="campaign" class="flex items-center gap-2">
            <Button
              v-if="campaign.campaign_status === 'processing'"
              variant="faded"
              color="amber"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.DETAIL.ACTIONS.PAUSE')"
              :is-loading="isTransitioning"
              @click="handlePause"
            />
            <Button
              v-if="campaign.campaign_status === 'paused'"
              variant="faded"
              color="teal"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.DETAIL.ACTIONS.RESUME')"
              :is-loading="isTransitioning"
              @click="handleResume"
            />
            <Button
              v-if="!isTerminal"
              variant="faded"
              color="ruby"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.DETAIL.ACTIONS.CANCEL')"
              :is-loading="isTransitioning"
              @click="handleCancel"
            />
            <Button
              v-if="hasFailedRecipients"
              variant="faded"
              color="blue"
              size="sm"
              :label="t('CAMPAIGN.WHATSAPP.DETAIL.ACTIONS.RETRY_FAILED')"
              :is-loading="isTransitioning"
              @click="handleRetryFailed"
            />
          </div>
        </div>
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="flex flex-col w-full max-w-5xl gap-6 py-4 mx-auto">
        <div v-if="!campaign" class="flex items-center justify-center py-10">
          <Spinner />
        </div>
        <template v-else>
          <CampaignAnalyticsSummary :analytics="analytics" />
          <RecipientsTable
            v-model:status-filter="statusFilter"
            v-model:current-page="currentPage"
            :recipients="recipients"
            :meta="recipientsMeta"
            :is-loading="isFetchingRecipients"
          />
        </template>
      </div>
    </main>
  </section>
</template>
